import * as React from 'react';
import { cn } from '../utils/cn';

export interface UploadFile {
  uid: string;
  name: string;
  status?: 'uploading' | 'done' | 'error' | 'removed';
  url?: string;
  originFileObj?: File;
}

export interface CustomRequestOptions {
  file: File;
  onSuccess?: (body: any) => void;
  onError?: (err: Error) => void;
  onProgress?: (event: { percent: number }) => void;
}

export interface UploadProps {
  /** Accepted file types (e.g. "image/*") */
  accept?: string;
  /** Allow multiple files */
  multiple?: boolean;
  /** Show upload list (default false) */
  showUploadList?: boolean;
  /** Custom upload handler — replaces default browser upload */
  customRequest?: (options: CustomRequestOptions) => void;
  /** Change callback */
  onChange?: (info: { file: UploadFile; fileList: UploadFile[] }) => void;
  /** Called before upload — return false to cancel */
  beforeUpload?: (file: File) => boolean | Promise<boolean>;
  /** Open file dialog on click (default true) */
  openFileDialogOnClick?: boolean;
  /** HTML id */
  id?: string;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** Trigger element (button, text, etc.) */
  children?: React.ReactNode;
}

export function Upload({
  accept,
  multiple = false,
  showUploadList = false,
  customRequest,
  onChange,
  beforeUpload,
  openFileDialogOnClick = true,
  id,
  className,
  style,
  children,
}: UploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [fileList, setFileList] = React.useState<UploadFile[]>([]);

  const handleClick = () => {
    if (openFileDialogOnClick && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // beforeUpload check
      if (beforeUpload) {
        const ok = await beforeUpload(file);
        if (ok === false) continue;
      }

      const uploadFile: UploadFile = {
        uid: `upload-${Date.now()}-${i}`,
        name: file.name,
        status: 'uploading',
        originFileObj: file,
      };

      const newList = [...fileList, uploadFile];
      setFileList(newList);

      if (customRequest) {
        customRequest({
          file,
          onSuccess: (body) => {
            uploadFile.status = 'done';
            setFileList((prev) => [...prev]);
            onChange?.({ file: uploadFile, fileList: newList });
          },
          onError: (err) => {
            uploadFile.status = 'error';
            setFileList((prev) => [...prev]);
            onChange?.({ file: uploadFile, fileList: newList });
          },
        });
      }

      onChange?.({ file: uploadFile, fileList: newList });
    }

    // Reset input so same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={cn('inline-block', className)} style={style}>
      <div onClick={handleClick} className="cursor-pointer">
        {children}
      </div>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="hidden"
      />
      {showUploadList && fileList.length > 0 && (
        <ul className="mt-2 space-y-1">
          {fileList.map((f) => (
            <li
              key={f.uid}
              className={cn(
                'flex items-center gap-2 text-sm font-body px-2 py-1 rounded-sm',
                f.status === 'error' && 'text-destructive bg-destructive/10',
                f.status === 'done' && 'text-success bg-success/10',
                f.status === 'uploading' && 'text-muted-foreground bg-gray-50'
              )}
            >
              <span className="truncate">{f.name}</span>
              {f.status === 'uploading' && (
                <span className="text-xs text-muted-foreground">Laddar upp...</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
